import { Button, Table } from 'antd'
import React, { useEffect } from 'react'
import { CSVLink } from 'react-csv'
import { buildCsvString } from 'src/utils/csv'
import { useDispatch, useSelector } from 'react-redux'
import FadeIn from 'src/components/styled/FadeIn'
import BarChart from 'src/screens/orders/spend-over-time/BarChart'
import LineChart from 'src/screens/orders/spend-over-time/LineChart'
import styled from 'styled-components/macro'
import dayjs from 'dayjs'
import { fetchSpendOverTimeTotals } from 'src/features/OrdersSlice'
import {
  selectVatPreference,
  VAT_PREFERENCE,
} from 'src/features/VatPreferenceSlice'
import {
  pickSpendOverTimeAmount,
  pickVatTotal,
  withVatLabel,
} from 'src/utils/vatPreference'
import useIsBlocked from 'src/hooks/useIsBlocked'
import PermissionBlockScreen from 'src/components/shared/PermissionBlockScreen'
import { mergeSpendBySupplierName } from 'src/utils/spendBySupplierRows'
import { formatGbp } from 'src/utils/formatGbp'

const SpendOverTime = () => {
  const blocked = useIsBlocked('spend-over-time')

  if (blocked) {
    return <PermissionBlockScreen />
  }

  const dispatch = useDispatch()
  const venue = useSelector(state => state.venue.venue)
  const filter = useSelector(state => state.filter.orders)
  const spendTotals = useSelector(state => state.orders.spendOverTimeTotals)
  const vatPreference = useSelector(selectVatPreference)

  useEffect(() => {
    dispatch(
      fetchSpendOverTimeTotals(
        venue._id,
        filter?.startDate,
        filter?.endDate,
        filter?.suppliers,
        filter?.types,
      ),
    )
  }, [filter])

  const grandTotal = pickVatTotal(vatPreference, {
    exVat: spendTotals.grandTotal,
    incVat: spendTotals.grandTotalIncVat,
  })

  const supplierRows = mergeSpendBySupplierName(spendTotals.bySuppliers)

  const supplierTable = supplierRows.map(item => ({
    key: item.key,
    supplier: item.supplierName,
    totalAmountNoVAT: formatGbp(pickSpendOverTimeAmount(
        vatPreference,
        item,
      )),
  }))

  const supplierTableWithTotal = [
    ...supplierTable.sort((a, b) => a.supplier.localeCompare(b.supplier)),
    {
      key: 'total',
      supplier: 'Total',
      totalAmountNoVAT: formatGbp(grandTotal),
    },
  ]

  const suppliersHeaders = [
    { label: 'Supplier', key: 'supplier' },
    {
      label: withVatLabel('Spend on OMS', vatPreference),
      key: 'totalAmountNoVAT',
    },
  ]

  const supplierChart = supplierRows.map(item => ({
    key: item.key,
    supplier: item.supplierName,
    label: item.supplierName,
    id: item.supplierName,
    value: pickSpendOverTimeAmount(vatPreference, item),
  }))

  const monthTable =
    spendTotals.byMonths?.map(item => ({
      key: `${item.year}/${item.month}`,
      monthName: dayjs(`${item.year}-${item.month}`).format('MMMM YYYY'),
      totalAmountNoVAT: formatGbp(pickSpendOverTimeAmount(
        vatPreference,
        item,
      )),
    })) || []

  const monthTableWithTotal = [
    ...monthTable,
    {
      key: 'total',
      monthName: 'Total',
      totalAmountNoVAT: formatGbp(grandTotal),
    },
  ]

  const monthChart =
    spendTotals.byMonths?.map(item => ({
      title: venue.title,
      monthNumber: `${item.year}/${item.month}`,
      monthName: dayjs(`${item.year}-${item.month}`).format('MMMM YYYY'),
      totalAmountNoVAT: pickSpendOverTimeAmount(vatPreference, item),
    })) || []

  const totalVenueHeaders = [
    { label: venue.title, key: 'monthName' },
    {
      label: withVatLabel('Spend on OMS', vatPreference),
      key: 'totalAmountNoVAT',
    },
  ]

  const vatSubtitle =
    vatPreference === VAT_PREFERENCE.INC
      ? 'VAT is included in all totals'
      : 'VAT is excluded in all totals'

  return (
    <Wrapper>
      <Card>
        <CardHeader>
          <HeaderContent>
            <CardTitle>Spending per Supplier</CardTitle>
            <CardSubtitle>{vatSubtitle}</CardSubtitle>
          </HeaderContent>
          <Button>
            <CSVLink
              data={buildCsvString([
                suppliersHeaders.map(h => h.label),
                ...supplierTableWithTotal.map(r => [
                  r.supplier,
                  r.totalAmountNoVAT,
                ]),
              ])}
              filename={`oms-products-spending-per-supplier_${venue.title}.csv`}
            >
              Download CSV
            </CSVLink>
          </Button>
        </CardHeader>
        <CardContent>
          <ChartWrapper>
            <BarChart data={supplierChart} types={[]} />
          </ChartWrapper>
          <TableWrapper>
            <Table
              dataSource={supplierTableWithTotal}
              pagination={false}
              scroll={
                supplierTableWithTotal.length > 3 ? { x: true, y: 220 } : {}
              }
              columns={[
                {
                  title: 'Supplier',
                  key: 'supplier',
                  dataIndex: 'supplier',
                  sorter: (a, b) =>
                    String(a.supplier).localeCompare(String(b.supplier)),
                },
                {
                  title: withVatLabel('Spend on OMS', vatPreference),
                  key: 'totalAmountNoVAT',
                  dataIndex: 'totalAmountNoVAT',
                },
              ]}
            />
          </TableWrapper>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <HeaderContent>
            <CardTitle>Spending by month</CardTitle>
            <CardSubtitle>{vatSubtitle}</CardSubtitle>
          </HeaderContent>
          <Button>
            <CSVLink
              data={buildCsvString([
                totalVenueHeaders.map(h => h.label),
                ...monthTableWithTotal.map(r => [
                  r.monthName,
                  r.totalAmountNoVAT,
                ]),
              ])}
              filename={`oms-products-spending-by-month_${venue.title}.csv`}
            >
              Download CSV
            </CSVLink>
          </Button>
        </CardHeader>
        <CardContent>
          <ChartWrapper>
            <LineChart data={monthChart} venue={venue.title} />
          </ChartWrapper>
          <TableWrapper>
            <Table
              dataSource={monthTableWithTotal}
              pagination={false}
              scroll={monthTableWithTotal.length > 3 ? { x: true, y: 220 } : {}}
              columns={[
                {
                  title: venue.title,
                  key: 'monthName',
                  dataIndex: 'monthName',
                  className: 'restrictShrink',
                },
                {
                  title: withVatLabel('Spend on OMS', vatPreference),
                  key: 'totalAmountNoVAT',
                  dataIndex: 'totalAmountNoVAT',
                  className: 'restrictShrink',
                },
              ]}
            />
          </TableWrapper>
        </CardContent>
      </Card>
    </Wrapper>
  )
}

const Wrapper = styled(FadeIn)`
  .ant-table-tbody {
    tr:last-child {
      background-color: ${({ theme }) => theme.colours.bbqBlack};
      color: ${({ theme }) => theme.colours.mayoWhite};
      &:hover {
        color: ${({ theme }) => theme.colours.bbqBlack};
      }
    }
    .price-amoung {
      white-space: nowrap;
    }
  }
`

const Card = styled.section`
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 32px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 24px;
`

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const CardTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
`

const CardSubtitle = styled.p`
  margin: 0;
  color: #6b6b6b;
  font-size: 13px;
`

const CardContent = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;
  min-height: 300px;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const ChartWrapper = styled.div`
  flex: 1;
  min-width: 0;
  height: 300px;
`

const TableWrapper = styled.div`
  min-width: 375px;
  max-width: 400px;
  @media (max-width: 768px) {
    min-width: 100%;
    max-width: 100%;
  }
`

export default SpendOverTime
